import { api } from '@shared/api';
import type { ActiveSessionSummary, DashboardStats } from '@shared/types';

interface BackendDashboardStats {
  streak: number;
  words_in_memory: number;
  week_delta: number;
  // ВАЖНО: поле без цифр. На бэке Pydantic alias_generator=to_camel конвертит
  // snake → camel так, что цифры не получают разделитель ('accuracy_7d' →
  // 'accuracy7d'), а наш response-интерцептор обратно в snake тоже их не
  // делит. Чтобы оба слоя соглашались — храним просто `accuracy`.
  accuracy: number;
  today_done: number;
  today_goal: number;
  // Виджет «Сегодня» (2026-10-21); старый бэкенд их не присылает.
  week_days?: boolean[];
  today_target?: { module_id: string; title: string; ready_count: number } | null;
  // Серия и «что повторить» на главной (2026-10-21).
  day_counted?: boolean;
  plan?: {
    ready: number;
    repeat: number;
    mistakes: number;
    stale: number;
    new: number;
    done_today: number;
    tomorrow: number;
    tomorrow_first: string[];
    modules_to_repeat: number;
    modules_with_words: number;
  } | null;
  // Новая главная (2026-10-22): когда серия даст жетоны; старый бэкенд не присылает.
  streak_reward?: { in_days: number; tokens: number } | null;
}

/** GET /api/v1/profile/dashboard-stats — настоящие метрики юзера, не mock.
 *  Если бэк недоступен — пробрасываем ошибку (react-query сам покажет
 *  isError и юзер увидит skeleton/empty состояние). */
export async function fetchDashboardStats(): Promise<DashboardStats> {
  const { data } = await api.get<BackendDashboardStats>('/profile/dashboard-stats');
  return {
    streak: data.streak,
    wordsInMemory: data.words_in_memory,
    weekDelta: data.week_delta,
    accuracy: data.accuracy,
    todayDone: data.today_done,
    todayGoal: data.today_goal,
    weekDays: data.week_days ?? [],
    todayTarget: data.today_target
      ? { moduleId: data.today_target.module_id, title: data.today_target.title, readyCount: data.today_target.ready_count }
      : null,
    dayCounted: data.day_counted ?? (data.week_days?.[6] === true),
    plan: data.plan
      ? {
          ready: data.plan.ready,
          repeat: data.plan.repeat,
          mistakes: data.plan.mistakes,
          stale: data.plan.stale,
          new: data.plan.new,
          doneToday: data.plan.done_today,
          tomorrow: data.plan.tomorrow,
          tomorrowFirst: data.plan.tomorrow_first ?? [],
          modulesToRepeat: data.plan.modules_to_repeat,
          modulesWithWords: data.plan.modules_with_words,
        }
      : null,
    streakReward: data.streak_reward ? { inDays: data.streak_reward.in_days, tokens: data.streak_reward.tokens } : null,
  };
}

interface BackendActiveSessionItem {
  item_id: string;
  status: 'pending' | 'done' | 'to_repeat';
  attempts: number;
}

interface BackendActiveSession {
  id: string;
  module_id: string | null;
  status: 'in_progress' | 'is_ready_to_boss' | 'completed' | 'abandoned';
  total: number;
  remaining: number;
  items?: BackendActiveSessionItem[];
  cram_mode: string | null;
}

/** Последняя незавершённая тренировка, включая её модуль и прогресс. */
export async function fetchActiveSessionSummary(): Promise<ActiveSessionSummary | null> {
  const { data } = await api.get<BackendActiveSession | null>('/sessions/active');
  if (!data?.module_id || !data.cram_mode) return null;
  const items = data.items ?? [];
  let correct = 0;
  let seen = 0;
  let toRedo = 0;
  for (const it of items) {
    if (it.status === 'done') correct += 1;
    if (it.status === 'to_repeat') toRedo += 1;
    seen += it.attempts;
  }
  return {
    id: data.id,
    moduleId: data.module_id,
    // active-эндпоинт по контракту отдаёт только активные статусы.
    status: data.status === 'is_ready_to_boss' ? 'is_ready_to_boss' : 'in_progress',
    total: data.total,
    remaining: data.remaining,
    done: Math.max(0, data.total - data.remaining),
    correct,
    wrong: Math.max(0, seen - correct),
    seen,
    toRedo,
    cramMode: data.cram_mode ?? null,
  };
}
